import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../api/client";
import { AuthWordmark } from "./AuthWordmark";
import { PasswordInput } from "./PasswordInput";
import { PasswordStrength } from "./PasswordStrength";

type AccountType = "organization" | "individual";

interface InviteResponse {
  valid: boolean;
  reason?: string;
  email?: string;
  accountIntent?: "individual" | "firm";
  firmName?: string | null;
  orgName?: string | null;
}

type Invite =
  | { status: "loading" }
  | { status: "missing" }
  | { status: "invalid"; reason?: string }
  | { status: "ok"; email: string; accountType: AccountType; orgName: string };

export function AccountSetupPage() {
  const { state, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("invite");

  const [invite, setInvite] = useState<Invite>({ status: "loading" });
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("organization");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Validate + prefill from the invite before showing the form.
  useEffect(() => {
    if (!token) {
      setInvite({ status: "missing" });
      return;
    }
    let cancelled = false;
    api.get<InviteResponse>(`/api/auth/invite/${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        if (!res.valid) {
          setInvite({ status: "invalid", reason: res.reason });
          return;
        }
        const type: AccountType = res.accountIntent === "individual" ? "individual" : "organization";
        setAccountType(type);
        setOrgName(res.orgName || res.firmName || "");
        setInvite({ status: "ok", email: res.email || "", accountType: type, orgName: res.orgName || res.firmName || "" });
      })
      .catch(() => {
        if (!cancelled) setInvite({ status: "invalid" });
      });
    return () => { cancelled = true; };
  }, [token]);

  // Already signed in → straight to the app.
  if (state.kind === "authenticated") {
    return <Navigate to="/app" replace />;
  }

  const isOrg = accountType === "organization";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (isOrg && !orgName.trim()) { setError("Please name your organization."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }

    setError("");
    setLoading(true);
    try {
      await register({
        inviteToken: token,
        name: name.trim(),
        password,
        accountType,
        orgName: isOrg ? orgName.trim() : null,
      });
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create your account.");
      setLoading(false);
    }
  };

  // ---- Invite still loading ----
  if (invite.status === "loading") {
    return (
      <div className="auth-screen">
        <div className="auth-card-wrap">
          <AuthWordmark />
          <div className="card">
            <div className="card-body">
              <p className="auth-sub">Checking your invite…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- No / bad invite → invite-gated, so send them to request access ----
  if (invite.status === "missing" || invite.status === "invalid") {
    const reason =
      invite.status === "missing"
        ? "Setting up an account is invite-only during the private beta."
        : invite.reason === "expired"
          ? "This invite link has expired."
          : invite.reason === "already_used"
            ? "This invite has already been used."
            : "This invite link is no longer valid.";
    return (
      <div className="auth-screen">
        <div className="auth-card-wrap">
          <AuthWordmark />
          <div className="card">
            <div className="card-body auth-stack">
              <div className="auth-title">Invite required</div>
              <p className="auth-sub">{reason}</p>
              <Link to="/beta" className="btn btn-primary btn-block">
                Request beta access
              </Link>
            </div>
          </div>
          <p className="auth-foot">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
          <p className="auth-back">
            <Link to="/">← Back to home</Link>
          </p>
        </div>
      </div>
    );
  }

  // ---- Valid invite → account setup form ----
  return (
    <div className="auth-screen">
      <div className="auth-card-wrap">
        <AuthWordmark />

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-body beta-wizard auth-stack">
              <div>
                <div className="auth-title">Set up your account</div>
                <p className="auth-sub" style={{ marginTop: 6 }}>
                  Welcome. Finish setting up to get into Engineering Studio.
                </p>
              </div>

              {error && <div className="form-error" role="alert">{error}</div>}

              <label className="field">
                <span className="field-label">Account type</span>
                <div className="seg" role="group" aria-label="Account type">
                  <button
                    type="button"
                    aria-pressed={isOrg}
                    className={`seg-btn${isOrg ? " is-on" : ""}`}
                    onClick={() => setAccountType("organization")}
                  >
                    Organization
                  </button>
                  <button
                    type="button"
                    aria-pressed={!isOrg}
                    className={`seg-btn${!isOrg ? " is-on" : ""}`}
                    onClick={() => setAccountType("individual")}
                  >
                    Individual
                  </button>
                </div>
              </label>

              <label className="field">
                <span className="field-label">Email</span>
                <input
                  type="email"
                  className="field-input"
                  value={invite.email}
                  readOnly
                  disabled
                />
              </label>

              <label className="field">
                <span className="field-label">Your name</span>
                <input
                  type="text"
                  className="field-input"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  required
                />
              </label>

              {isOrg && (
                <label className="field">
                  <span className="field-label">Organization name</span>
                  <input
                    type="text"
                    className="field-input"
                    autoComplete="organization"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    required
                  />
                </label>
              )}

              <label className="field">
                <span className="field-label">Password</span>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  autoComplete="new-password"
                  required
                />
                <PasswordStrength password={password} />
              </label>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? "Creating account…" : "Create account"}
              </button>
            </div>
          </div>
        </form>

        <p className="auth-foot">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
        <p className="auth-back">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
